import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileCode, Copy, Download, CheckCircle2, Check, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'

/** One generated file. `content` is present once the set has been loaded. */
export interface TaskCodeFile {
    filePath: string
    content?: string | null
    description?: string | null
    sequence?: number | null
    size?: number | null
}

/** A generated code set for one runbook task — what the engineer downloads and runs. */
export interface TaskCodeSetLike {
    entryFile?: string | null
    format?: string | null
    description?: string | null
    status?: string | null
    verifyMethod?: string | null
    files?: TaskCodeFile[] | null
}

/**
 * Roughly how many bytes a file is, for the list. Uses the stored size when the set came
 * from a listing that has one, and falls back to the content length.
 */
function fileSize(f: TaskCodeFile): number {
    if (typeof f.size === 'number' && f.size > 0) return f.size
    return f.content ? f.content.length : 0
}

function formatBytes(n: number): string {
    if (!n) return ''
    if (n < 1024) return `${n} B`
    return `${(n / 1024).toFixed(1)} KB`
}

/**
 * The generated files for one task: pick a file on the left, read it on the right.
 *
 * A task whose Commands field says `.\Restore-VmFromVaultSentinel.ps1 -VCenter …` is telling
 * the engineer to run a script the platform wrote — so the script has to be visible from the
 * same place, or the command line is an instruction to run a file nobody can find. Generation
 * can produce several files (an entry script plus helpers), which is why this is a list and
 * not a single code box.
 */
export function TaskCodeFiles({ set, compact = false }: { set: TaskCodeSetLike; compact?: boolean }) {
    const { t } = useTranslation()
    const files = useMemo(() => set.files ?? [], [set.files])
    const [activePath, setActivePath] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)

    // Open on the entry file — it is the one the Commands field names, so it is the one the
    // engineer came here to read.
    useEffect(() => {
        if (files.length === 0) {
            setActivePath(null)
            return
        }
        const entry = set.entryFile && files.some((f) => f.filePath === set.entryFile) ? set.entryFile : files[0].filePath
        setActivePath((prev) => (prev && files.some((f) => f.filePath === prev) ? prev : entry))
    }, [files, set.entryFile])

    const active = files.find((f) => f.filePath === activePath) ?? files[0]

    const handleCopy = async () => {
        if (!active?.content) return
        try {
            await navigator.clipboard.writeText(active.content)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
        } catch {
            /* clipboard blocked — the content is on screen and selectable anyway */
        }
    }

    // Downloading writes the file the engineer is about to run. Same content that is on
    // screen, under the name the Commands field refers to.
    const handleDownload = () => {
        if (!active?.content) return
        const blob = new Blob([active.content], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = active.filePath.split('/').pop() || 'script.txt'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    if (files.length === 0) {
        return (
            <div className="text-xs text-muted-foreground py-2">
                {t('objects.runbook.noGeneratedFiles', 'No script has been generated for this task yet.')}
            </div>
        )
    }

    return (
        <div className="space-y-2">
            {/* What this set is: verified or draft, what checked it, what it does */}
            <div className="flex flex-wrap items-center gap-2 text-xs">
                {set.status === 'verified' ? (
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        {t('objects.runbook.codeVerified', 'verified')}
                    </Badge>
                ) : (
                    <Badge variant="secondary">{t('objects.runbook.codeDraft', 'draft')}</Badge>
                )}
                {set.format && <Badge variant="outline" className="font-mono">{set.format}</Badge>}
                {set.verifyMethod && <span className="text-muted-foreground">{set.verifyMethod}</span>}
                {set.description && <span className="text-muted-foreground truncate">— {set.description}</span>}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
                {/* File list */}
                <div className="sm:w-56 shrink-0 space-y-1">
                    {files.map((f) => {
                        const isEntry = f.filePath === set.entryFile
                        const isActive = f.filePath === active?.filePath
                        return (
                            <button
                                key={f.filePath}
                                type="button"
                                onClick={() => setActivePath(f.filePath)}
                                className={`w-full text-left rounded border px-2 py-1.5 transition-colors ${
                                    isActive
                                        ? 'bg-muted border-ring'
                                        : 'border-transparent hover:bg-muted/60'
                                }`}
                            >
                                <span className="flex items-center gap-1.5 min-w-0">
                                    <FileCode className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                    <span className="font-mono text-xs truncate">{f.filePath}</span>
                                </span>
                                <span className="flex items-center gap-2 pl-5 text-[11px] text-muted-foreground">
                                    {isEntry && (
                                        <span className="text-purple-600 dark:text-purple-400">
                                            {t('objects.runbook.codeEntry', 'run this one')}
                                        </span>
                                    )}
                                    {fileSize(f) > 0 && <span>{formatBytes(fileSize(f))}</span>}
                                </span>
                            </button>
                        )
                    })}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-mono text-xs text-muted-foreground truncate">
                            {active?.filePath}
                            {active?.description ? ` — ${active.description}` : ''}
                        </span>
                        <div className="flex items-center gap-1 shrink-0">
                            <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={handleCopy} disabled={!active?.content}>
                                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                            </Button>
                            <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={handleDownload} disabled={!active?.content}>
                                <Download className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>
                    <pre
                        className={`text-xs bg-muted rounded-md p-3 overflow-auto whitespace-pre font-mono ${
                            compact ? 'max-h-64' : 'max-h-[55vh]'
                        }`}
                    >
                        {active?.content ?? ''}
                    </pre>
                </div>
            </div>
        </div>
    )
}

interface TaskCodeDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    taskNo: number | null
    taskText?: string
    /** Already-loaded set (e.g. generated this session and not yet saved). */
    set?: TaskCodeSetLike | null
    /** Fetches the set for a saved runbook. Ignored when `set` is supplied. */
    onFetch?: (taskNo: number) => Promise<TaskCodeSetLike | null>
}

/** The same file list and viewer, in a dialog, for reading a task's script from a card. */
export function TaskCodeDialog({ open, onOpenChange, taskNo, taskText, set, onFetch }: TaskCodeDialogProps) {
    const { t } = useTranslation()
    const [fetched, setFetched] = useState<TaskCodeSetLike | null>(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!open || taskNo == null || set || !onFetch) return
        let cancelled = false
        setLoading(true)
        Promise.resolve(onFetch(taskNo))
            .then((r) => { if (!cancelled) setFetched(r) })
            .catch(() => { if (!cancelled) setFetched(null) })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
    }, [open, taskNo, set, onFetch])

    const effective = set ?? fetched

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileCode className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        {t('objects.runbook.generatedFiles', 'Generated files')}
                        {taskNo != null && (
                            <span className="text-muted-foreground font-normal">
                                — {t('objects.runbook.task', 'Task')} {taskNo}
                            </span>
                        )}
                    </DialogTitle>
                    <DialogDescription className="line-clamp-2">
                        {taskText ||
                            t(
                                'objects.runbook.generatedFilesHelp',
                                'The script this task runs. Placeholders are bound per object; credentials are supplied when it runs and are never stored.'
                            )}
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="py-10 flex items-center justify-center text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('common.loading', 'Loading...')}
                    </div>
                ) : effective ? (
                    <TaskCodeFiles set={effective} />
                ) : (
                    <div className="py-8 text-sm text-muted-foreground">
                        {t('objects.runbook.noGeneratedFiles', 'No script has been generated for this task yet.')}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
